import { useEffect, useState } from "react";
import { useNavigate, useParams, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, Building2 } from "lucide-react";

import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import Breadcrumbs from "@/components/Breadcrumbs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ContactsEditor from "@/components/ContactsEditor";
import { readBack } from "@/lib/nav";

const EMPTY = {
  name: "",
  contacts: [],
  billing_address: { street: "", city: "", postcode: "", country: "" },
  email_reports_default: true,
  email_delivery_mode: "to",
  on_demand_service: false,
  notes: "",
  active: true,
};

const DELIVERY_MODE_HELP = {
  to: "Every recipient on the To line — everyone sees who else received the report.",
  cc: "First recipient on To, others on Cc — visible cc list, sender relationship preserved.",
  bcc: "First recipient on To, others on Bcc — recipients don't see each other (privacy default).",
};

export default function CustomerForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const backCtx = readBack(searchParams);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  const cancelTo = backCtx?.to || (isEdit ? `/customers/${id}` : "/customers");

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data } = await api.get(`/customers/${id}`);
        setForm({ ...EMPTY, ...data, billing_address: { ...EMPTY.billing_address, ...(data.billing_address || {}) } });
      } catch (err) {
        toast.error(formatApiError(err));
        navigate("/customers", { replace: true });
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isEdit, navigate]);

  const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));
  const updateAddress = (patch) => setForm((prev) => ({ ...prev, billing_address: { ...prev.billing_address, ...patch } }));

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      if (isEdit) {
        const { data } = await api.patch(`/customers/${id}`, form);
        toast.success("Customer updated");
        navigate(backCtx?.to || `/customers/${data.id}`);
      } else {
        const { data } = await api.post("/customers", form);
        toast.success("Customer created");
        navigate(backCtx?.to || `/customers/${data.id}`);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="p-6 space-y-6" data-testid="customer-form-page">
      <Breadcrumbs
        items={[
          { label: "Customers", to: "/customers" },
          ...(isEdit ? [{ label: form.name || "Customer", to: `/customers/${id}` }, { label: "Edit" }] : [{ label: "New" }]),
        ]}
      />
      <PageHeader
        testId="customer-form-header"
        title={isEdit ? `Edit ${form.name}` : "New customer"}
        subtitle={isEdit ? "Update customer details, billing and notes." : "Add a new customer to the directory."}
        actions={
          <Button asChild variant="outline" size="sm" data-testid="customer-form-back">
            <Link to={cancelTo}><ArrowLeft className="h-4 w-4" /> Back</Link>
          </Button>
        }
      />

      <form onSubmit={submit} className="space-y-6 max-w-4xl">
        <Card className="rounded-md">
          <CardHeader>
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Building2 className="h-4 w-4 text-primary" /> Details
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5 md:col-span-2">
              <Label htmlFor="name">Company name</Label>
              <Input id="name" value={form.name} onChange={(e) => update({ name: e.target.value })} required data-testid="customer-form-name" />
            </div>
            <div className="space-y-1.5 flex items-end">
              <div className="flex items-center justify-between w-full rounded-sm border border-border bg-card p-2.5">
                <div>
                  <div className="text-sm font-medium">Email service reports</div>
                  <div className="text-xs text-muted-foreground">Send PDF reports on job close.</div>
                </div>
                <Switch checked={form.email_reports_default} onCheckedChange={(v) => update({ email_reports_default: v })} data-testid="customer-form-email-reports" />
              </div>
            </div>
            <div className="space-y-1.5 flex items-end">
              <div className="flex items-center justify-between w-full rounded-sm border border-border bg-card p-2.5">
                <div>
                  <div className="text-sm font-medium">On-demand service</div>
                  <div className="text-xs text-muted-foreground">Show on the Call-to-Book list instead of scheduled Due Soon.</div>
                </div>
                <Switch checked={form.on_demand_service} onCheckedChange={(v) => update({ on_demand_service: v })} data-testid="customer-form-on-demand" />
              </div>
            </div>
            {form.email_reports_default && (
              <div className="space-y-1.5 md:col-span-2" data-testid="customer-form-delivery-mode-block">
                <Label htmlFor="delivery-mode" className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Report delivery mode
                </Label>
                <Select
                  value={form.email_delivery_mode}
                  onValueChange={(v) => update({ email_delivery_mode: v })}
                >
                  <SelectTrigger id="delivery-mode" data-testid="customer-form-delivery-mode">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="to" data-testid="customer-form-delivery-mode-to">To — all recipients visible</SelectItem>
                    <SelectItem value="cc" data-testid="customer-form-delivery-mode-cc">Cc — primary on To, rest on Cc</SelectItem>
                    <SelectItem value="bcc" data-testid="customer-form-delivery-mode-bcc">Bcc — primary on To, rest on Bcc</SelectItem>
                  </SelectContent>
                </Select>
                <div className="text-[11px] text-muted-foreground">
                  {DELIVERY_MODE_HELP[form.email_delivery_mode] || DELIVERY_MODE_HELP.to}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="rounded-md">
          <CardHeader>
            <CardTitle className="text-sm font-medium">Billing address</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5 md:col-span-2">
              <Label htmlFor="street">Street</Label>
              <Input id="street" value={form.billing_address.street} onChange={(e) => updateAddress({ street: e.target.value })} data-testid="customer-form-street" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="city">City</Label>
              <Input id="city" value={form.billing_address.city} onChange={(e) => updateAddress({ city: e.target.value })} data-testid="customer-form-city" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="postcode">Postcode</Label>
              <Input id="postcode" value={form.billing_address.postcode} onChange={(e) => updateAddress({ postcode: e.target.value })} data-testid="customer-form-postcode" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="country">Country</Label>
              <Input id="country" value={form.billing_address.country} onChange={(e) => updateAddress({ country: e.target.value })} data-testid="customer-form-country" />
            </div>
          </CardContent>
        </Card>

        <ContactsEditor
          value={form.contacts}
          onChange={(contacts) => update({ contacts })}
          testIdPrefix="customer-form-contacts"
        />

        <Card className="rounded-md">
          <CardHeader>
            <CardTitle className="text-sm font-medium">Notes</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Textarea rows={4} value={form.notes} onChange={(e) => update({ notes: e.target.value })} data-testid="customer-form-notes" placeholder="Anything the team should know about this customer…" />
            <div className="flex items-center justify-between rounded-sm border border-border bg-card p-3">
              <div>
                <div className="text-sm font-medium">Active</div>
                <div className="text-xs text-muted-foreground">Inactive customers are hidden from the default list.</div>
              </div>
              <Switch checked={form.active} onCheckedChange={(v) => update({ active: v })} data-testid="customer-form-active" />
            </div>
          </CardContent>
        </Card>

        <div className="flex items-center justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => navigate(cancelTo)} data-testid="customer-form-cancel">Cancel</Button>
          <Button type="submit" disabled={saving} data-testid="customer-form-submit">
            <Save className="h-4 w-4" />
            {saving ? "Saving…" : isEdit ? "Save changes" : "Create customer"}
          </Button>
        </div>
      </form>
    </div>
  );
}
